import { SystemEntity } from '@erp/shared-types';
import { Avatar, AvatarFallback } from '@/components/ui/avatar';
import {
  HoverCard,
  HoverCardTrigger,
  HoverCardContent,
} from '@/components/ui/hover-card';
import { cn } from '@/lib/utils';
import { getEntityIcon, getEntityDisplayName } from '../utils/index';
import { SystemEntityPreview } from '../preview-ui';

interface SystemEntityNanoProps {
  entityType: SystemEntity;
  entityId: string;
  className?: string;
}

function FallbackNano({
  entityType,
  entityId,
  className,
}: SystemEntityNanoProps) {
  const Icon = getEntityIcon(entityType);
  const displayName = getEntityDisplayName(entityType);

  return (
    <HoverCard openDelay={200} closeDelay={100}>
      <HoverCardTrigger asChild>
        <div className="inline-block cursor-pointer">
          <Avatar
            className={cn('size-8 rounded-sm', className)}
            title={displayName}
          >
            <AvatarFallback className="rounded-sm bg-muted">
              <Icon className="size-4 text-muted-foreground" />
            </AvatarFallback>
          </Avatar>
        </div>
      </HoverCardTrigger>
      <HoverCardContent className="w-auto p-0" align="start">
        <SystemEntityPreview entityType={entityType} entityId={entityId} />
      </HoverCardContent>
    </HoverCard>
  );
}

/**
 * Component renderers for each system entity type
 * Maps entity types to their nano UI representations
 */
const entityRenderers: Partial<
  Record<SystemEntity, (props: SystemEntityNanoProps) => React.ReactNode>
> = {
  // Entity-specific renderers will be added here over time
  // Example:
  // [SystemEntity.AP_INVOICE]: (props) => <APInvoiceNano {...props} />,
};

/**
 * Nano UI component for system entities
 * Renders a small, square avatar-based representation
 */
export function SystemEntityNano({
  entityType,
  entityId,
  className,
}: SystemEntityNanoProps) {
  const Renderer = entityRenderers[entityType];

  if (Renderer) {
    return <>{Renderer({ entityType, entityId, className })}</>;
  }

  return (
    <FallbackNano
      entityType={entityType}
      entityId={entityId}
      className={className}
    />
  );
}
